'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { X, Heart, Wind, Sparkles, Phone, Star, Coffee } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';




interface PopupMessage {
  id: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  body: string;
  cta?: string;
  ctaAction?: () => void;
}

const POPUP_MESSAGES: PopupMessage[] = [
  {
    id: 'breathing-reminder',
    icon: Wind,
    iconColor: '#003399',
    iconBg: 'rgba(0,51,153,0.1)',
    title: 'Take a breath 🌬️',
    body: 'Try the 4-7-8 technique: inhale for 4 seconds, hold for 7, exhale for 8. It activates your body\'s natural calm response.',
    cta: 'Try it now',
  },
  {
    id: 'encouragement-1',
    icon: Star,
    iconColor: '#E3AE54',
    iconBg: 'rgba(227,174,84,0.12)',
    title: 'You\'re doing great 🌟',
    body: 'Every check-in you complete is a step forward. Awareness is the first and most powerful tool in your journey.',
  },
  {
    id: 'urge-surfing',
    icon: Heart,
    iconColor: '#8b5cf6',
    iconBg: 'rgba(139,92,246,0.1)',
    title: 'Urge surfing tip 🏄',
    body: 'Urges are like waves — they peak and then pass. You don\'t have to act on them. Try riding it out for just 10 minutes.',
  },
  {
    id: 'helpline-rwanda',
    icon: Phone,
    iconColor: '#22c55e',
    iconBg: 'rgba(34,197,94,0.1)',
    title: 'Support is always available',
    body: 'If you\'re struggling, you\'re not alone. Rwanda\'s Umubano helpline is free and confidential: dial 3029 anytime.',
    cta: 'Save this number',
  },
  {
    id: 'grounding',
    icon: Sparkles,
    iconColor: '#003399',
    iconBg: 'rgba(0,51,153,0.1)',
    title: '5-4-3-2-1 Grounding ✨',
    body: 'Name 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, 1 you can taste. Brings you back to the present.',
  },
  {
    id: 'self-care',
    icon: Coffee,
    iconColor: '#E3AE54',
    iconBg: 'rgba(227,174,84,0.12)',
    title: 'Small acts of self-care matter',
    body: 'A glass of water, a short walk, or a few minutes outside can genuinely shift your mood. What\'s one small thing you can do right now?',
  },
  {
    id: 'progress-reminder',
    icon: Star,
    iconColor: '#22c55e',
    iconBg: 'rgba(34,197,94,0.1)',
    title: 'Progress, not perfection 💚',
    body: 'Recovery isn\'t a straight line. Every day you show up — even on hard days — counts. Be kind to yourself today.',
  },
  {
    id: 'diary-prompt',
    icon: Sparkles,
    iconColor: '#8b5cf6',
    iconBg: 'rgba(139,92,246,0.1)',
    title: 'A moment to reflect 📝',
    body: 'Writing down your thoughts — even just a sentence — can help you understand your patterns and feel more in control.',
    cta: 'Open diary',
  },
];

// Show popup after 90 seconds, then every 8 minutes
const INITIAL_DELAY = 90_000;
const REPEAT_INTERVAL = 8 * 60_000;

export default function WellbeingPopup() {
  const [visible, setVisible] = useState(false);
  const [currentMsg, setCurrentMsg] = useState<PopupMessage | null>(null);
  const [msgIndex, setMsgIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const showNext = useCallback(() => {
    setMsgIndex(prev => {
      const next = (prev + 1) % POPUP_MESSAGES.length;
      setCurrentMsg(POPUP_MESSAGES[next]);
      return next;
    });
    setVisible(true);
  }, []);

  useEffect(() => {
    // Pick a random starting message
    const startIdx = Math.floor(Math.random() * POPUP_MESSAGES.length);
    setMsgIndex(startIdx);
    setCurrentMsg(POPUP_MESSAGES[startIdx]);

    const initialTimer = setTimeout(() => {
      if (!dismissed) setVisible(true);
    }, INITIAL_DELAY);

    return () => clearTimeout(initialTimer);
  }, [dismissed]);

  useEffect(() => {
    if (!visible) return;
    const repeatTimer = setInterval(() => {
      showNext();
    }, REPEAT_INTERVAL);
    return () => clearInterval(repeatTimer);
  }, [visible, showNext]);

  const handleDismiss = () => {
    setVisible(false);
  };

  if (!visible || !currentMsg) return null;

  const Icon = currentMsg.icon;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-xs w-full mx-4 sm:mx-0"
      style={{ animation: 'slideInUp 0.35s ease-out' }}
    >
      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        className="bg-card rounded-2xl shadow-xl border border-border p-4 relative"
        style={{ boxShadow: '0 8px 32px rgba(0,51,153,0.12), 0 2px 8px rgba(0,0,0,0.08)' }}
      >
        {/* Close */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>

        <div className="flex items-start gap-3 pr-5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: currentMsg.iconBg }}
          >
            <Icon size={18} style={{ color: currentMsg.iconColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-700 text-foreground leading-snug">{currentMsg.title}</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{currentMsg.body}</p>
            {currentMsg.cta && (
              <button
                onClick={() => { currentMsg.ctaAction?.(); handleDismiss(); }}
                className="mt-2 text-xs font-600 underline underline-offset-2 transition-opacity hover:opacity-70"
                style={{ color: currentMsg.iconColor }}
              >
                {currentMsg.cta}
              </button>
            )}
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-1 mt-3 justify-center">
          {POPUP_MESSAGES.map((_, i) => (
            <div
              key={`dot-${i}`}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === msgIndex ? 16 : 5,
                height: 5,
                background: i === msgIndex ? currentMsg.iconColor : 'var(--border)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
